import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import classNames from "classnames/bind";
import { useRecruitment } from "../../../context/RecruitmentContext";
import { findCampaignById } from "../../../utils/constants/config";
import { hasRecruitmentSessionActive } from "../../../utils/helps";
import CampaignChatPanel from "../../desktop/chatbot-recruiment/components/CampaignChatPanel";
import MobileHeader from "./components/header/MobileHeader";
import MobileCampaignDrawer from "./Modal/MobileCampaignDrawer";
import QuillEditor from "../../../components/QuillEditor/QuillEditor";
import styles from "./ChatBotRecruimentMobile.module.sass";

const cx = classNames.bind(styles);

const GENERAL_CHAT_KEY = "__general__";

const parseCampaignRoute = (pathname) => {
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length === 0) {
    return { mode: "list" };
  }

  if (segments[0] === "chat" && segments.length === 1) {
    return { mode: "chat-general" };
  }

  if (segments.length === 2 && segments[1] === "chat") {
    return { mode: "chat-campaign", campaignId: segments[0] };
  }

  if (segments.length === 2 && segments[1] === "apply") {
    return { mode: "apply", campaignId: segments[0] };
  }

  if (segments.length === 1 && segments[0] !== "chat") {
    return { mode: "detail", campaignId: segments[0] };
  }

  return { mode: "list" };
};

const DetailSection = ({ icon, title, content, values }) => (
  <div className={cx("detailSection")}>
    <h3>
      <span className={cx("sectionIcon")}>{icon}</span>
      {title}
    </h3>
    {content ? (
      <QuillEditor value={content} className={cx("detailQuill")} />
    ) : (
      <ul>
        {values?.map((item, index) => (
          <li key={`${title}-${index}`}>{item}</li>
        ))}
      </ul>
    )}
  </div>
);

const DetailView = ({ campaign, onClose, onApply }) => (
  <div className={cx("detailPage")}>
    <div className={cx("detailHeader")}>
      <button
        type="button"
        className={cx("detailClose")}
        onClick={onClose}
        aria-label="Đóng"
      >
        ×
      </button>
      <h1 className={cx("detailTitle")}>{campaign.title}</h1>
      <p className={cx("detailDepartment")}>{campaign.department}</p>
      <button type="button" className={cx("applyButton")} onClick={onApply}>
        <span aria-hidden="true">📄</span>
        Ứng tuyển ngay
      </button>
    </div>
    <div className={cx("detailContent")}>
      <DetailSection
        icon="💼"
        title="Mô tả công việc"
        content={campaign.description}
      />
      <DetailSection
        icon="📋"
        title="Yêu cầu ứng viên"
        content={campaign.requirements}
      />
      <DetailSection icon="🎁" title="Quyền lợi" content={campaign.benefits} />
      <DetailSection
        icon="🕐"
        title="Thời gian làm việc"
        content={campaign.workingTime}
      />
    </div>
  </div>
);

const GeneralDetailView = ({ onClose, onOpenChat }) => (
  <div className={cx("detailPage")}>
    <div className={cx("detailHeader")}>
      <button
        type="button"
        className={cx("detailClose")}
        onClick={onClose}
        aria-label="Đóng"
      >
        ×
      </button>
      <h1 className={cx("detailTitle")}>Trợ lý tuyển dụng AI</h1>
      <p className={cx("detailDepartment")}>Trò chuyện chung</p>
      <button type="button" className={cx("applyButton")} onClick={onOpenChat}>
        <span aria-hidden="true">💬</span>
        Bắt đầu trò chuyện
      </button>
    </div>
    <div className={cx("detailContent")}>
      <DetailSection
        icon="✨"
        title="Hỗ trợ chung"
        values={[
          "Hỏi về quy trình tuyển dụng",
          "Hỏi về hồ sơ, CV và phỏng vấn",
          "Tìm hiểu các vị trí đang tuyển",
        ]}
      />
    </div>
  </div>
);

const MobileCampaignPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const route = parseCampaignRoute(location.pathname);
  const campaignId = route.campaignId;
  const isApplyRoute = route.mode === "apply";

  const {
    campaigns,
    loading,
    chatStarted,
    isCandidateRegistered,
    openCandidateModal,
    requestChatAccess,
    openChatCampaignIds,
    activeChatCampaignId,
    activateChatCampaign,
    selectChatCampaign,
  } = useRecruitment();

  const campaign = findCampaignById(campaigns, campaignId);
  const prevCampaignIdRef = useRef(campaignId);

  useEffect(() => {
    if (!isApplyRoute || isCandidateRegistered) return;
    openCandidateModal(campaignId, { showCvUpload: true });
  }, [campaignId, isApplyRoute, isCandidateRegistered, openCandidateModal]);

  useEffect(() => {
    if (!isCandidateRegistered || !campaignId) {
      prevCampaignIdRef.current = campaignId;
      return;
    }

    const campaignChanged =
      String(prevCampaignIdRef.current) !== String(campaignId);
    prevCampaignIdRef.current = campaignId;

    if (
      campaignChanged &&
      !openChatCampaignIds.includes(String(campaignId))
    ) {
      activateChatCampaign(campaignId);
    }
  }, [
    activateChatCampaign,
    campaignId,
    isCandidateRegistered,
    openChatCampaignIds,
  ]);

  const hasStoredChatSession = campaignId
    ? hasRecruitmentSessionActive(campaignId)
    : hasRecruitmentSessionActive(GENERAL_CHAT_KEY);

  const showChat = useMemo(
    () =>
      chatStarted &&
      isCandidateRegistered &&
      (route.mode === "chat-general" ||
        route.mode === "chat-campaign" ||
        (route.mode === "detail" &&
          (openChatCampaignIds.length > 0 || hasStoredChatSession))),
    [
      chatStarted,
      isCandidateRegistered,
      route.mode,
      openChatCampaignIds,
      hasStoredChatSession,
    ],
  );

  useEffect(() => {
    if (!showChat) return undefined;

    const html = document.documentElement;
    const body = document.body;
    const app = document.getElementById("app");
    const scrollY = window.scrollY;

    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      appOverflow: app?.style.overflow ?? "",
    };

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    if (app) app.style.overflow = "hidden";

    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.width = prev.bodyWidth;
      if (app) app.style.overflow = prev.appOverflow;
      window.scrollTo(0, scrollY);
    };
  }, [showChat]);

  const handleOpenChat = () => {
    if (requestChatAccess(campaignId)) {
      navigate(campaignId ? `/${campaignId}/chat` : "/chat");
    }
  };

  const handleApply = () => {
    if (!isCandidateRegistered) {
      openCandidateModal(campaignId, { showCvUpload: true });
      return;
    }
    requestChatAccess(campaignId);
    navigate(campaignId ? `/${campaignId}/chat` : "/chat");
  };

  const handleSelectCampaign = (id) => {
    setDrawerOpen(false);
    if (selectChatCampaign(id)) {
      navigate(`/${id}/chat`);
    }
  };

  const handleOpenGeneralChat = () => {
    setDrawerOpen(false);
    if (requestChatAccess(null)) {
      navigate("/chat");
    }
  };

  const handleCloseDetail = () => {
    navigate("/");
  };

  if (!loading && campaignId && !campaign && campaigns.length > 0) {
    return <Navigate to={`/${campaigns[0].id}`} replace />;
  }

  if (loading) {
    return (
      <div className={cx("page")}>
        <MobileHeader
          showChatCta={!isCandidateRegistered}
          onChatClick={handleOpenChat}
        />
        <div className={cx("emptyState")}>
          <p>Đang tải thông tin vị trí...</p>
        </div>
      </div>
    );
  }

  if (showChat) {
    const activeCampaign =
      route.mode === "chat-general"
        ? null
        : findCampaignById(campaigns, activeChatCampaignId) || campaign;

    return (
      <div className={cx("page", "pageChat")}>
        <MobileHeader
          variant="chat"
          onMenuClick={() => setDrawerOpen(true)}
        />
        <div className={cx("chatBody")}>
          <div className={cx("chatPanelWrap")}>
            <CampaignChatPanel
              key={activeChatCampaignId ?? campaign?.id ?? "general"}
              campaign={activeCampaign}
              onApply={handleApply}
              showApplyButton={isCandidateRegistered}
              showQuickActions={false}
              isMobile
            />
          </div>
        </div>
        <MobileCampaignDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          campaigns={campaigns}
          activeCampaignId={activeChatCampaignId ?? campaignId}
          isGeneralChatActive={route.mode === "chat-general"}
          onOpenGeneralChat={handleOpenGeneralChat}
          onSelect={handleSelectCampaign}
        />
      </div>
    );
  }

  if (route.mode === "chat-general" && !showChat) {
    return (
      <GeneralDetailView
        onClose={() => navigate("/")}
        onOpenChat={handleOpenChat}
      />
    );
  }

  if (!campaign) {
    return <Navigate to="/" replace />;
  }

  return (
    <DetailView
      campaign={campaign}
      onClose={handleCloseDetail}
      onApply={handleApply}
    />
  );
};

export default MobileCampaignPage;
